import {
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccessConditionEffect,
  type Condition,
  type AccessCondition,
  type Role,
} from '@repo/api-gw-sdk';
import { useMemo, useState } from 'react';

import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { InclusiveOptionsMenu } from './inclusiveOptionsMenu';
import { getRoleConditionSupportedProperties } from './utils';

import { ConditionEditor } from '../conditions/ConditionEditor';
import { BottomDialog } from '../dialogs/bottomDialog';
import { PanelFooter } from '../panels/panelFooter';
import { PanelHeader } from '../panels/panelHeader';

export const CustomRoleConditionEditorDialog = ({
  open,
  onSave,
  onCancel,
  roleCondition,
  accessConditions,
}: {
  roleCondition: AccessCondition | undefined;
  accessConditions: Role['accessConditions'];
  open: boolean;
  onCancel: () => void;
  onSave: (name: string, condition: Condition, isInclusive: boolean) => void;
}) => {
  const { SensitivityAnnotations: enableSensitivityAnnotations } =
    useFeatureFlags();
  const supportedProperties = useMemo(
    () =>
      getRoleConditionSupportedProperties({
        enableSensitivityAnnotations: !!enableSensitivityAnnotations,
      }),
    [enableSensitivityAnnotations]
  );
  const [name, setName] = useState(roleCondition?.id || '');
  const [isInclusive, setInclusive] = useState(
    roleCondition
      ? roleCondition.effect === AccessConditionEffect.RULE_EFFECT_INCLUSIVE
      : false
  );
  const [condition, setCondition] = useState(
    roleCondition ? structuredClone(roleCondition.condition) : undefined
  );

  const saveEnabled =
    name &&
    condition &&
    (roleCondition
      ? roleCondition.id === name
        ? true
        : !accessConditions?.find((x) => x.id === name)
      : !accessConditions?.find((x) => x.id === name));

  return (
    <BottomDialog open={open} isFullScreen={true}>
      <Stack className='h-full'>
        <PanelHeader
          hideBorder
          title={
            roleCondition ? 'Edit Access Condition' : 'Create Access Condition'
          }
          onClose={onCancel}
        />
        <Stack gap='12px' className='px-[24px] overflow-y-auto' flexGrow={1}>
          <Stack
            direction='row'
            className='mt-[24px] mx-[16px]'
            flexGrow={1}
            gap='40px'
          >
            <Stack flexGrow={1} gap='24px'>
              <Stack direction='row' alignItems='center' gap='8px'>
                <InclusiveOptionsMenu
                  isInclusive={isInclusive}
                  setInclusive={setInclusive}
                />
                <Typography variant='body1'>
                  access to resources that meet these conditions:
                </Typography>
              </Stack>

              <ConditionEditor
                options={{
                  supportGrouping: true,
                  supportedProperties,
                }}
                condition={condition}
                onChange={(condition) => setCondition(condition)}
              />
            </Stack>
            <Divider orientation='vertical' flexItem />
            <Stack className='w-[388px]'>
              <FormControl size='small'>
                <FormLabel>Name</FormLabel>
                <TextField
                  placeholder='Example: No production with PII'
                  className='my-[12px]'
                  size='small'
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              </FormControl>
            </Stack>
          </Stack>
        </Stack>
        <PanelFooter
          primaryButton={{
            'data-testid': 'save-condition',
            text: roleCondition ? 'Save Changes' : 'Create Condition',
            onClick: () => onSave(name, condition!, isInclusive),
            disabled: !saveEnabled,
          }}
          secondaryButton={{
            'data-testid': 'cancel-edit-condition',
            text: 'Cancel',
            onClick: onCancel,
          }}
        />
      </Stack>
    </BottomDialog>
  );
};
